// Rendimiento por agente. Tabla plana (no chart): es la alternativa accesible
// y exacta de por_agente — nombres, conteos y tiempos sin estimar contra ejes.
// El backend ya la manda ordenada por resueltas desc; no se reordena acá.
//
// Con onSelectAgent presente, cada fila es además la puerta de entrada al
// drill-down de casos del agente (AgentCasesDrawer): fila clickeable con
// affordance (hover + chevron) y operable por teclado (Enter/Espacio).

import { memo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { Avatar } from "../../../../components/ui/Avatar";
import { useAgentNames, initialsOfName } from "./useAgentNames";
import { formatInt, formatMinutes } from "./format";

const HEADER_CELL_CLS = "text-[11px] font-semibold uppercase tracking-wide text-fg-muted pb-2";

/**
 * @param {object} props
 * @param {Array<{agent_id: string, asignadas: number, resueltas: number, tiempo_resolucion_avg_min: number|null}>} props.rows
 * @param {(agentId: string) => void} [props.onSelectAgent] - abre el drill-down de casos del agente
 * @param {string} [props.className]
 */
function AgentTable({ rows, onSelectAgent, className }) {
  const { t } = useTranslation();
  // Mapa id→nombre cargado una vez al montar; si el fetch de usuarios falla la
  // tabla degrada a ids truncados sin romperse.
  const { agentNameOf } = useAgentNames();

  const data = rows || [];
  const hasData = data.length > 0;
  const interactive = typeof onSelectAgent === "function";

  return (
    <Card padding="md" className={className}>
      <h2 className="text-sm font-semibold text-fg mb-3">{t("Rendimiento por agente")}</h2>

      {hasData ? (
        // Scrollea la tabla dentro de su contenedor, nunca la página.
        // A6 · `ui-table-scroller` (src/scss/_mobile-primitives.scss) pinta el
        // degradado de borde mientras quede tabla oculta: a 390px el contenedor
        // mide 252px para 560px de tabla y, como los scrollbars móviles son
        // overlay, no había NINGUNA señal de que siguiera a la derecha.
        <div className="overflow-x-auto ui-table-scroller">
          <table className="w-full min-w-[560px] border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={cn(HEADER_CELL_CLS, "text-left pr-3")}>
                  {t("Agente")}
                </th>
                <th scope="col" className={cn(HEADER_CELL_CLS, "text-right px-3")}>
                  {t("Asignadas")}
                </th>
                <th scope="col" className={cn(HEADER_CELL_CLS, "text-right px-3")}>
                  {t("Resueltas")}
                </th>
                <th scope="col" className={cn(HEADER_CELL_CLS, "text-right pl-3")}>
                  {t("Tiempo medio de resolución")}
                </th>
                {interactive && (
                  // Columna angosta del chevron: solo affordance, sin título.
                  <th scope="col" className={cn(HEADER_CELL_CLS, "w-8")} aria-hidden="true" />
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const name = agentNameOf(row.agent_id);
                // Sin agent_id (bucket "sin asignar") no hay drill-down posible.
                const clickable = interactive && !!row.agent_id;
                return (
                  // La fila NO lleva role="button": pisar la semántica de <tr>
                  // rompe la navegación por tabla de los lectores de pantalla
                  // (dejan de anunciar celdas/columnas). El control accesible
                  // es el BOTÓN del nombre; el onClick de la fila queda como
                  // atajo de puntero, invisible para el árbol de accesibilidad.
                  <tr
                    key={row.agent_id || "none"}
                    className={cn(
                      "h-11 border-b border-border",
                      clickable && "cursor-pointer hover:bg-bg-subtle transition-colors",
                    )}
                    // Los handlers arrow inline en el map son seguros acá: la
                    // fila no es un componente memoizado (regla PERF).
                    {...(clickable ? { onClick: () => onSelectAgent(row.agent_id) } : {})}
                  >
                    <td className="pr-3">
                      {clickable ? (
                        <button
                          type="button"
                          aria-label={t("Ver casos de {{name}}", { name })}
                          onClick={(e) => {
                            // El click de la fila ya dispara el drawer; sin el
                            // stop, teclado+puntero sobre el botón lo abriría 2x.
                            e.stopPropagation();
                            onSelectAgent(row.agent_id);
                          }}
                          className="flex items-center gap-2.5 min-w-0 w-full text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        >
                          <Avatar size="sm" tone="neutral" initials={initialsOfName(name)} />
                          <span className="text-sm text-fg truncate">{name}</span>
                          {/* A6 · Duplicado del chevron pegado al nombre: en
                              móvil el de la última columna cae 232px fuera de
                              pantalla y la fila parecía un dato muerto en vez
                              de la puerta al detalle del agente. */}
                          <ChevronRight
                            className="md:hidden w-4 h-4 shrink-0 text-fg-muted"
                            aria-hidden="true"
                          />
                        </button>
                      ) : (
                        <span className="flex items-center gap-2.5 min-w-0">
                          <Avatar size="sm" tone="neutral" initials={initialsOfName(name)} />
                          <span className="text-sm text-fg truncate">{name}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 text-right text-sm text-fg tabular-nums">
                      {formatInt(row.asignadas)}
                    </td>
                    <td className="px-3 text-right text-sm text-fg tabular-nums">
                      {formatInt(row.resueltas)}
                    </td>
                    <td className="pl-3 text-right text-sm text-fg tabular-nums">
                      {formatMinutes(row.tiempo_resolucion_avg_min)}
                    </td>
                    {interactive && (
                      <td className="pl-2 text-right">
                        {clickable && (
                          <ChevronRight
                            className="w-4 h-4 text-fg-muted inline-block"
                            aria-hidden="true"
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-[120px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin asignaciones en el período.")}
        </div>
      )}
    </Card>
  );
}

export default memo(AgentTable);
